import React, { useEffect, useState, useRef } from 'react';
import { emailApiService, EmailMessage, EmailAccount } from '../../services/email/emailApiService';
import { emailPollingService } from '../../services/email/emailPollingService';
import { Reply, Paperclip, FileText, Download, Loader2, Send, X } from 'lucide-react';

interface EmailThreadViewProps {
    threadId: string;
    onClose?: () => void;
}

export const EmailThreadView: React.FC<EmailThreadViewProps> = ({ threadId, onClose }) => {
    const [messages, setMessages] = useState<EmailMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [replying, setReplying] = useState(false);
    const [replyBody, setReplyBody] = useState('');
    const [sendingReply, setSendingReply] = useState(false);
    const [accounts, setAccounts] = useState<EmailAccount[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<string>('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchMessages = async () => {
        try {
            const data = await emailApiService.getMessages(threadId);
            setMessages(data);
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setMessages([]);
        setLoading(true);
        setReplying(false);
        fetchMessages();
        loadAccounts();

        const unsubscribe = emailPollingService.subscribe(() => {
            fetchMessages();
        });

        return () => unsubscribe();
    }, [threadId]);

    useEffect(() => {
        // Scroll to bottom on new messages
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const loadAccounts = async () => {
        try {
            const data = await emailApiService.getAccounts();
            setAccounts(data);
            if (data.length > 0) {
                const defaultAcc = data.find(a => a.is_default && a.is_active) || data.find(a => a.is_active);
                if (defaultAcc) setSelectedAccount(defaultAcc.id);
            }
        } catch (error) {
            console.error('Error loading accounts:', error);
        }
    };

    // ... (keep existing useEffects)

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setReplyAttachments(prev => [...prev, ...Array.from(e.target.files || [])]);
        }
    };

    const removeAttachment = (index: number) => {
        setReplyAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleReply = async () => {
        if (!replyBody.trim() && replyAttachments.length === 0) return;
        if (!selectedAccount) {
            alert('Please select an email account to reply from');
            return;
        }

        try {
            setSendingReply(true);

            // Get recipients from the last inbound message or use the original sender
            const lastInbound = [...messages].reverse().find(m => m.direction === 'inbound');
            const to = lastInbound ? [lastInbound.sender_email] : []; // Fallback needed

            if (to.length === 0) {
                // If no inbound, maybe we are replying to our own thread?
                // This logic might need improvement to track original recipients
                // For now, try to find any other participant
                const otherParticipants = messages
                    .filter(m => m.direction === 'inbound')
                    .map(m => m.sender_email);

                if (otherParticipants.length > 0) {
                    to.push(otherParticipants[0]);
                } else {
                    // Last resort: if it's an outbound thread, maybe we can't reply easily without knowing who it was sent to originally if not in message list
                    // But getMessages returns recipient_emails for outbound messages too.
                    const lastOutbound = [...messages].reverse().find(m => m.direction === 'outbound');
                    if (lastOutbound && lastOutbound.recipient_emails.length > 0) {
                        to.push(...lastOutbound.recipient_emails);
                    } else {
                        alert('Could not determine recipient');
                        return;
                    }
                }
            }

            await emailApiService.replyToThread({
                accountId: selectedAccount,
                threadId,
                to,
                bodyHtml: replyBody.replace(/\n/g, '<br>'),
                attachments: replyAttachments
            });

            setReplyBody('');
            setReplyAttachments([]);
            setReplying(false);
            fetchMessages(); // Refresh immediately
        } catch (error) {
            console.error('Error sending reply:', error);
            alert('Failed to send reply');
        } finally {
            setSendingReply(false);
        }
    };

    // ... (keep handleParseAttachment)

    const handleParseAttachment = async (attachmentId: string) => {
        if (!selectedAccount) {
            alert('Please select an email account to access the attachment');
            return;
        }

        try {
            // Show loading state for this attachment?
            // For now just alert
            alert('Parsing started... check console for result');
            const result = await emailApiService.parseAttachment(attachmentId, selectedAccount);
            console.log('Parsed result:', result);

            if (result.confidence > 70) {
                alert(`Successfully parsed offer from ${result.retailerName} and auto-populated offer.`);
            } else {
                alert('Parsing completed with low confidence. Offer was auto-populated but please review manually.');
            }

            fetchMessages(); // Refresh to update status
        } catch (error: any) {
            console.error('Error parsing attachment:', error);
            alert(`Parsing failed: ${error.message}`);
        }
    };

    if (loading) {
        return <div className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" /></div>;
    }

    return (
        <div className="flex flex-col h-full bg-white">
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-6">
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`flex flex-col ${msg.direction === 'outbound' ? 'items-end' : 'items-start'}`}
                    >
                        <div
                            className={`max-w-[85%] min-w-0 overflow-hidden rounded-lg p-4 shadow-sm ${msg.direction === 'outbound'
                                ? 'bg-blue-50 border border-blue-100'
                                : 'bg-white border border-gray-200'
                                }`}
                        >
                            <div className="flex justify-between items-start mb-2 gap-4">
                                <div className="text-sm">
                                    <span className="font-semibold text-gray-900">
                                        {msg.direction === 'outbound' ? 'Me' : msg.sender_email}
                                    </span>
                                    <span className="text-gray-500 mx-2">to</span>
                                    <span className="text-gray-600 truncate max-w-[200px] inline-block align-bottom">
                                        {msg.recipient_emails?.join(', ')}
                                    </span>
                                </div>
                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                    {new Date(msg.sent_at || msg.received_at).toLocaleString()}
                                </span>
                            </div>

                            <div
                                className="prose prose-sm max-w-none text-gray-800 mb-4 overflow-x-auto"
                                dangerouslySetInnerHTML={{ __html: msg.body_html || msg.snippet }}
                            />

                            {msg.attachments && msg.attachments.length > 0 && (
                                <div className="border-t pt-3 mt-2 space-y-2">
                                    <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                                        <Paperclip className="w-3 h-3" />
                                        {msg.attachments.length} Attachments
                                    </p>
                                    <div className="grid gap-2">
                                        {msg.attachments.map(att => (
                                            <div key={att.id} className="flex items-center justify-between bg-gray-50 p-2 rounded border text-sm">
                                                <div className="flex items-center gap-2 overflow-hidden">
                                                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                    <span className="truncate">{att.filename}</span>
                                                    <span className="text-gray-400 text-xs">
                                                        ({att.size_bytes ? (att.size_bytes / 1024).toFixed(1) : '0'} KB)
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={async (e) => {
                                                            e.preventDefault();
                                                            if (!selectedAccount) {
                                                                alert('Please select an account to download attachments.');
                                                                return;
                                                            }
                                                            try {
                                                                // Use the service to download with auth headers
                                                                const blob = await emailApiService.downloadAttachment(att.id, selectedAccount);
                                                                const url = window.URL.createObjectURL(blob);
                                                                const a = document.createElement('a');
                                                                a.href = url;
                                                                a.download = att.filename;
                                                                document.body.appendChild(a);
                                                                a.click();
                                                                window.URL.revokeObjectURL(url);
                                                                document.body.removeChild(a);
                                                            } catch (error) {
                                                                console.error('Download failed:', error);
                                                                alert('Failed to download attachment. Please try again.');
                                                            }
                                                        }}
                                                        className={`text-gray-500 hover:text-blue-600 p-1 rounded hover:bg-blue-50 transition-colors ${!selectedAccount ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        title="Download Attachment"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            <div className="border-t p-4 bg-gray-50">
                {replying ? (
                    <div className="bg-white border rounded-lg shadow-sm p-4">
                        <div className="mb-3">
                            <select
                                value={selectedAccount}
                                onChange={(e) => setSelectedAccount(e.target.value)}
                                className="text-sm border rounded p-1"
                            >
                                {accounts.map(acc => (
                                    <option key={acc.id} value={acc.id}>{acc.email}</option>
                                ))}
                            </select>
                        </div>
                        <textarea
                            value={replyBody}
                            onChange={(e) => setReplyBody(e.target.value)}
                            placeholder="Type your reply..."
                            className="w-full border rounded-md p-3 min-h-[100px] mb-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            autoFocus
                        />

                        {/* Attachment List */}
                        {replyAttachments.length > 0 && (
                            <div className="mb-3 space-y-2">
                                {replyAttachments.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                                        <span className="truncate">{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                                        <button onClick={() => removeAttachment(index)} className="text-red-500 hover:text-red-700">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-between items-center">
                            <div className="flex gap-2">
                                <input
                                    type="file"
                                    multiple
                                    className="hidden"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="text-gray-500 hover:text-blue-600 p-2 rounded hover:bg-gray-100"
                                    title="Attach files"
                                >
                                    <Paperclip className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setReplying(false)}
                                    className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReply}
                                    disabled={sendingReply || (!replyBody.trim() && replyAttachments.length === 0)}
                                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    Send Reply
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() => setReplying(true)}
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors w-full justify-center border border-dashed border-gray-300"
                    >
                        <Reply className="w-4 h-4" />
                        Reply to thread
                    </button>
                )}
            </div>
        </div>
    );
};
